/* Lo que sale del pañol hacia una persona, y su papel firmado (P-311).
 *
 * Nico, 05/10: *«La sección herramienta y entrega de elementos deberían ser la
 * misma y verse como lo hace herramientas. Al fin y al cabo salen tmb del
 * pañol pero el destinatario es un chofer y no un mecánico (única
 * diferencia)»*.
 *
 * Una ENTREGA (`panol_entregas`) es el papel: a quién, si es mecánico o
 * chofer, de qué pañol sale, la lista congelada de lo que se entregó y la foto
 * del papel firmado. Lo que sale del catálogo además es una fila de
 * `taller_prestamo` (con `entrega_id`): eso arma la caja y descuenta del
 * stock. Lo que se escribe a mano —un elemento que no está en el catálogo,
 * como en el acta de papel— vive sólo en la lista de la entrega.
 *
 * Acá va lo que se prueba sin base ni navegador. La base es la que manda
 * (`entregar_del_panol`); esto sólo ordena y nombra.
 */

import { pelar } from "./herramientas";

/* ── LOS PAÑOLES ──────────────────────────────────────────────────────────────
 *
 * Los mismos de `public.panoles`. Las siete sedes llevan el id de su sección
 * del menú (`panol-bsas` → `bsas`). Están escritos acá para no pedir a la base
 * una lista de nombres en cada pantalla; si alguien suma uno en la base sin
 * sumarlo acá, la herramienta igual se ve —con el id crudo— en vez de
 * desaparecer.
 *
 * En Don Torcuato hay CUATRO pañoles y no uno (Nico, 06/10 y 07/10: «en
 * Torcuato necesito… mi oficina, tractores, furgones y el fiscal»; «los
 * responsables no son los mismos, los pañoles tampoco»). Llevan el id de su
 * depósito.
 *
 * `bsas` es la ZONA COMÚN —«una zona en común donde ambos vean todas las cajas
 * y los chóferes»—: lo que se cargó antes de repartir sigue ahí, y `comun`
 * dice que también se entrega desde cualquiera de los cuatro. Conserva el id
 * porque es el de la sección del menú y el de todo lo ya cargado. */
export const PANOLES = [
  { id: "bsas", nombre: "Torcuato · Común", corto: "Común" },
  { id: "tractores", nombre: "Torcuato · Tractores", corto: "Tractores", comun: "bsas" },
  { id: "furgones", nombre: "Torcuato · Furgones", corto: "Furgones", comun: "bsas" },
  { id: "oficina-nico", nombre: "Torcuato · Oficina de Nico", corto: "Oficina Nico", comun: "bsas" },
  { id: "fiscal", nombre: "Torcuato · Depósito fiscal", corto: "Fiscal", comun: "bsas" },
  { id: "puerto", nombre: "Puerto", corto: "Puerto" },
  { id: "bahia-blanca", nombre: "Bahía Blanca", corto: "Bahía" },
  { id: "rio-gallegos", nombre: "Río Gallegos", corto: "R. Gallegos" },
  { id: "rio-grande", nombre: "Río Grande", corto: "R. Grande" },
  { id: "ushuaia", nombre: "Ushuaia", corto: "Ushuaia" },
  { id: "jow-kon", nombre: "Jow Kon", corto: "Jow Kon" },
];

export const PANOL_POR_DEFECTO = "bsas";

/* Los que no son una sede del menú sino una parte de Don Torcuato. */
export const PANOLES_DE_TORCUATO = ["tractores", "furgones", "oficina-nico", "fiscal"];

/* «sin» es el filtro de las que nadie asignó todavía: con un pañol elegido
   no se ven, y tienen que poder encontrarse para asignarlas. */
export const SIN_PANOL = "sin";

/* La zona común de un pañol, o `null` si no tiene. */
export const comunDe = (panol) => PANOLES.find((p) => p.id === panol)?.comun || null;

export function nombrePanol(id, { corto = false } = {}) {
  if (!id) return "Sin pañol";
  const p = PANOLES.find((x) => x.id === id);
  if (!p) return String(id);
  return corto ? p.corto : p.nombre;
}

/* Las herramientas de un pañol. `""` es todos; `SIN_PANOL` las que no tienen.
 * Se SACAN, no se apagan: cada pañol es otra pared, y una herramienta del
 * puerto apagada en la pared de BsAs se lee como «está, pero prestada». */
export function soloDelPanol(filas, panol) {
  if (!panol) return filas || [];
  if (panol === SIN_PANOL) return (filas || []).filter((f) => !f.panol);
  return (filas || []).filter((f) => f.panol === panol);
}

/* Lo que se puede elegir para entregar desde un pañol: lo suyo, lo de su zona
 * común y lo que todavía no tiene pañol (si no, una herramienta recién cargada
 * sin pañol no se podría entregar desde ningún lado). La base vuelve a
 * controlarlo. */
export const delPanolOSinAsignar = (filas, panol) =>
  (filas || []).filter((f) => !f.panol || !panol || f.panol === panol
    || f.panol === comunDe(panol));

/* Cuántas hay en cada pañol, para los botones del filtro. */
export function cuentaPorPanol(filas) {
  const c = { "": 0, [SIN_PANOL]: 0 };
  for (const f of filas || []) {
    c[""] += 1;
    const k = f.panol || SIN_PANOL;
    c[k] = (c[k] || 0) + 1;
  }
  return c;
}

/* ── MECÁNICO O CHOFER ────────────────────────────────────────────────────────
 *
 * La única diferencia (Nico). Sale del padrón: `personas.puesto` es CHOFER en
 * 191 activos; todo lo demás —TALLER, ADMINISTRATIVO, TRAFICO…— recibe
 * herramientas. Si el padrón no lo dice, mecánico: es lo que era la pantalla
 * hasta hoy. */
export const DESTINATARIOS = {
  mecanico: { rotulo: "Mecánico", que: "herramientas", icono: "🔧" },
  chofer: { rotulo: "Chofer", que: "elementos", icono: "🚚" },
};

export function destinatarioDe(persona) {
  return pelar(persona?.puesto) === "CHOFER" ? "chofer" : "mecanico";
}

/* Los elementos del acta de papel (`chofer_elementos.CATALOGO_COMUN` del bot):
   los nueve impresos, las tapas de guardabarro que se agregaban a mano y los
   cuatro que Nico dijo que más se entregan. Son un atajo para escribir rápido
   lo que todavía no está en el catálogo, igual que en el acta. */
export const ELEMENTOS_COMUNES = [
  "Crique botella 12T",
  "Balizas x2",
  "Llave de rueda con palanca",
  "Manija levanta cabina",
  "Perno de enganche",
  "Manual",
  "Cable de luces",
  "Alfombras",
  "Caja destornilladores",
  "Tapas de guardabarro",
  "Lonas",
  "Alargues",
  "Matafuegos",
  "Cadenas",
];

/* ── CORREGIR UN PAPEL A MANO (Nico, 07/10) ───────────────────────────────────
 *
 * «Hay varios listados que no leyó bien. Quiero corregir manualmente esos.»
 * Las actas de papel se cargaron leyendo un escaneo: todas quedaron con los
 * nueve elementos impresos, estuvieran tildados o no, y a varias les falta la
 * fecha o el interno.
 *
 * Se corrige LO ESCRITO A MANO. Lo que salió del catálogo (`herramienta_id`)
 * tiene además una fila en la caja y movió stock: eso se saca con «Sacar» en
 * la caja, no borrándole el renglón al papel —quedaría una llave en la caja
 * sin papel que la nombre—. Acá viaja intacto. */
export const esDelCatalogo = (it) => it?.herramienta_id != null;

export function itemsCorregidos(items) {
  const out = [];
  for (const it of items || []) {
    if (esDelCatalogo(it)) { out.push(it); continue; }
    const nombre = String(it?.nombre || "").replace(/\s+/g, " ").trim();
    if (!nombre) continue;
    const nota = String(it?.nota || "").replace(/\s+/g, " ").trim().slice(0, 200);
    out.push({ nombre, cantidad: Math.max(1, Math.floor(Number(it?.cantidad) || 1)),
               ...(nota ? { nota } : {}) });
  }
  return out;
}

/* Lo que va a la base, o `{ error }`. La fecha vacía es «el papel no la
   trae», que es un dato; una fecha de mañana es un dedazo. */
/* EL NOMBRE Y EL DNI TAMBIÉN (Nico, 08/10): «me deja corregir todo menos el
   nombre y apellido del chofer, ni agregarle el DNI». Las actas viejas traen
   el nombre como lo leyó el reconocimiento («LozIANO FELICIANO») y muchas son
   de gente que ya no está en el padrón: no hay a quién elegir, se escribe.
   Los dos son opcionales para quien llama: si no vienen, no se tocan. */
export const nombreDelPapel = (v) =>
  String(v || "").replace(/[«»]/g, "").replace(/\s+/g, " ").trim();

export function correccionDeEntrega(datos, hoy = diaAr0()) {
  const { interno, entregado_en: fecha, items } = datos || {};
  const limpios = itemsCorregidos(items);
  if (!limpios.length) return { error: "El papel no puede quedar sin ningún elemento." };
  const f = String(fecha || "").trim();
  if (f && !/^\d{4}-\d{2}-\d{2}$/.test(f)) return { error: "Esa fecha no se entiende." };
  if (f && f > hoy) return { error: "La fecha no puede ser posterior a hoy." };
  const cambios = {
    interno: String(interno || "").trim().toUpperCase() || null,
    entregado_en: f || null,
    items: limpios,
  };
  if (datos && "persona_nombre" in datos) {
    const nombre = nombreDelPapel(datos.persona_nombre);
    if (!nombre) return { error: "Falta el nombre y apellido." };
    cambios.persona_nombre = nombre.slice(0, 120);
  }
  if (datos && "persona_dni" in datos) {
    const crudo = String(datos.persona_dni || "").trim();
    const dni = crudo.replace(/[.\s]/g, "");
    if (crudo && !/^\d{7,8}$/.test(dni)) return { error: "El DNI son 7 u 8 números." };
    cambios.persona_dni = dni || null;
  }
  return { cambios };
}

function diaAr0() {
  return new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
}

/* ── EL PAPEL FIRMADO ─────────────────────────────────────────────────────────
 *
 * «Que se vea qué entregas tienen el papel firmado y cuáles faltan». Tres
 * estados y no dos: las 64 actas cargadas del papel escaneado (2022-2026)
 * SÍ están firmadas —el papel existe, lo escaneó sistemas—, sólo que la foto
 * no está acá. Decir «falta el papel» de esas sería mandar a buscar una firma
 * que ya está. */
export const ESTADOS_PAPEL = {
  firmado: { rotulo: "Papel firmado", corto: "Firmado", clase: "cst--ok", icono: "✅" },
  papel: { rotulo: "Firmado en papel, falta subir la foto", corto: "Sin foto", clase: "cst--papel", icono: "🗂️" },
  falta: { rotulo: "Falta el papel firmado", corto: "Falta firma", clase: "cst--falta", icono: "✍️" },
};

export function estadoPapel(entrega) {
  if (!entrega) return "falta";
  if (entrega.foto_path) return "firmado";
  if (String(entrega.origen || "").startsWith("acta-papel")) return "papel";
  return "falta";
}

export const faltaFoto = (e) => !e?.anulado_en && estadoPapel(e) !== "firmado";

/* ── LA LISTA DE LO QUE SE ENTREGA ────────────────────────────────────────────
 *
 * El carrito de una entrega: renglones del catálogo `{herramienta_id, nombre,
 * codigo, cantidad, tope}` o escritos a mano `{nombre, cantidad}`, cada uno
 * con su `nota` si la tiene (Andrés, 27/10: una observación por herramienta
 * entregada, sea por la herramienta o por cualquier otra razón). Sumar lo
 * mismo dos veces suma la cantidad: dos renglones «Martillo × 1» en el papel
 * se leen como un error. */
export function sumarRenglon(renglones, nuevo) {
  const xs = [...(renglones || [])];
  const mismo = (r) => (nuevo.herramienta_id
    ? r.herramienta_id === nuevo.herramienta_id
    : !r.herramienta_id && pelar(r.nombre) === pelar(nuevo.nombre));
  const i = xs.findIndex(mismo);
  const cant = Math.max(1, Number(nuevo.cantidad) || 1);
  if (i < 0) return [...xs, { ...nuevo, cantidad: tope(nuevo, cant) }];
  xs[i] = { ...xs[i], cantidad: tope(xs[i], xs[i].cantidad + cant) };
  return xs;
}

function tope(r, cant) {
  const t = Number(r.tope);
  return r.herramienta_id && Number.isFinite(t) && t > 0 ? Math.min(cant, t) : cant;
}

export function cambiarCantidad(renglones, i, cantidad) {
  return (renglones || []).map((r, j) => (j === i
    ? { ...r, cantidad: tope(r, Math.max(1, Number(cantidad) || 1)) }
    : r));
}

export function cambiarNota(renglones, i, nota) {
  return (renglones || []).map((r, j) => (j === i ? { ...r, nota: String(nota ?? "") } : r));
}

/* Lo que se le manda a `entregar_del_panol`: el catálogo por id (el nombre lo
   pone la base, que es la que lo congela) y lo escrito a mano por nombre. La
   observación va solo si dice algo. */
export const itemsParaLaBase = (renglones) =>
  (renglones || []).map((r) => {
    const it = r.herramienta_id
      ? { herramienta_id: r.herramienta_id, cantidad: Number(r.cantidad) || 1 }
      : { nombre: String(r.nombre || "").trim(), cantidad: Number(r.cantidad) || 1 };
    const nota = String(r.nota || "").trim();
    return nota ? { ...it, nota } : it;
  })
    .filter((r) => r.herramienta_id || r.nombre);

/* «Martillo × 2 (mango flojo)» para el papel y las listas. */
export const renglonTexto = (it) =>
  `${it?.nombre || "—"}${Number(it?.cantidad) > 1 ? ` × ${it.cantidad}` : ""}`
  + `${String(it?.nota || "").trim() ? ` (${String(it.nota).trim()})` : ""}`;

/* ── LO QUE TIENE UNA PERSONA QUE NO ES DEL CATÁLOGO ──────────────────────────
 *
 * Los renglones escritos a mano de sus entregas vigentes —las actas viejas,
 * lo que se cargó por WhatsApp, lo que no estaba en el catálogo—. Lo del
 * catálogo NO va acá: ya está en la caja por sus préstamos, y contarlo dos
 * veces mostraría dos martillos donde hay uno. */
export function sueltosDe(entregas) {
  const out = [];
  for (const e of entregas || []) {
    // Lo devuelto ya no es de la persona: el papel sigue existiendo (es la
    // prueba de la entrega) pero deja de armar la caja.
    if (!e || e.anulado_en || e.devuelto_en) continue;
    for (const it of Array.isArray(e.items) ? e.items : []) {
      if (it && !it.herramienta_id && String(it.nombre || "").trim()) {
        out.push({ nombre: it.nombre, cantidad: Number(it.cantidad) || 1,
                   ...(String(it.nota || "").trim() ? { nota: String(it.nota).trim() } : {}),
                   entrega_id: e.id, desde: e.entregado_en || null });
      }
    }
  }
  return out;
}

/* El papel de «todo lo que tiene hoy»: lo del catálogo (fichas de la caja) y
   lo escrito a mano de sus entregas, sin repetir. Es para las cajas que se
   armaron antes de que hubiera papel, o por WhatsApp. */
export function itemsDeLoQueTiene(contenido, sueltos) {
  const conNota = (it, nota) => (nota ? { ...it, nota } : it);
  const items = (contenido || []).map((f) => conNota({
    herramienta_id: f.herramienta_id, codigo: f.codigo || null,
    nombre: f.nombre, cantidad: f.cantidad || 1,
  }, (f.notas || []).join(" · ")));
  const vistos = new Set();
  for (const s of sueltos || []) {
    const k = pelar(s.nombre);
    if (vistos.has(k)) continue;
    vistos.add(k);
    items.push(conNota({ nombre: s.nombre, cantidad: s.cantidad || 1 }, s.nota));
  }
  return items;
}

/* «2026-10-05» → «05/10/2026». Las fechas de entrega son DÍAS (columna
   `date`), sin hora: pasarlas por `new Date` las corre un día para atrás en
   Argentina. */
export function diaAr(v) {
  const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/* Las rutas viejas de «Entrega de elementos» caen acá, en las cajas de los
   choferes. */
export const RUTA_ELEMENTOS = "/taller/herramientas?quien=chofer";

/* Quién abre la pantalla: las llaves de las DOS que se juntaron. Herramientas
   pedía `ver.taller`; Entrega de elementos además `ver.trafico` (tráfico tiene
   la otra punta del deslinde). Cargar sigue siendo `cargar.taller`. */
export const puedeVerEntregas = (permisos) =>
  Array.isArray(permisos) &&
  ["ver.taller", "ver.trafico", "cargar.taller"].some((p) => permisos.includes(p));

/* ── QUITAR LO QUE SE CARGÓ DE MÁS ────────────────────────────────────────────
 *
 * Nico, 06/10: *«En herramientas le cargué más de una vez a la misma caja sin
 * querer y quiero poder quitarla»*. Pasó de verdad: a Pérez Mario le quedó el
 * mismo papel dos veces —uno por WhatsApp y el «papel de lo que tiene hoy»
 * cinco minutos después— y su caja mostraba cada cadena repetida.
 *
 * QUITAR ES ANULAR, NO BORRAR. El papel queda en la base con quién y cuándo lo
 * anuló (lo sella `panol_entregas_sellar`); sale de la caja y de las listas.
 * Lo del catálogo que salió con ese papel se anula con él: si el papel «no
 * pasó», esas herramientas tampoco salieron del pañol y vuelven al stock.
 */

/* La huella de un papel: qué nombra y cuánto, sin importar el orden. Lo del
   catálogo por id (el nombre congelado puede variar entre dos papeles de la
   misma herramienta) y lo escrito a mano por nombre pelado. */
export function huellaPapel(entrega) {
  return (Array.isArray(entrega?.items) ? entrega.items : [])
    .filter((it) => it && (it.herramienta_id || String(it.nombre || "").trim()))
    .map((it) => `${it.herramienta_id ? `#${it.herramienta_id}` : pelar(it.nombre)}` +
                 `×${Number(it.cantidad) || 1}`)
    .sort()
    .join("|");
}

/* Los papeles REPETIDOS: los que nombran exactamente lo mismo que otro papel
   vigente de la misma persona cargado antes. `Map(id repetido → id del
   original)`. El original es el más viejo: es el que se cargó a propósito, y
   si alguno tiene la foto firmada, ése manda sobre los demás. Un papel vacío
   no es repetido de nada. */
export function papelesRepetidos(entregas) {
  const vigentes = (entregas || [])
    .filter((e) => e && !e.anulado_en && huellaPapel(e))
    .sort((a, b) => {
      // El firmado primero: nunca se sugiere quitar el que tiene la firma.
      if (!!a.foto_path !== !!b.foto_path) return a.foto_path ? -1 : 1;
      return String(a.creado_en || "").localeCompare(String(b.creado_en || ""))
        || (Number(a.id) || 0) - (Number(b.id) || 0);
    });
  const vistos = new Map();
  const out = new Map();
  for (const e of vigentes) {
    const k = `${String(e.persona_dni || e.persona_nombre || "").trim()}::${huellaPapel(e)}`;
    if (vistos.has(k)) out.set(e.id, vistos.get(k));
    else vistos.set(k, e.id);
  }
  return out;
}

/* Lo que pasa si se quita una caja entera, para decirlo ANTES de hacerlo.
 *
 * Una caja no es una fila: es lo asignado de una persona (préstamos
 * definitivos abiertos) más sus papeles vigentes. Quitarla:
 *   · anula sus papeles (no se borran: quedan con quién y cuándo);
 *   · lo del catálogo que salió con uno de esos papeles se ANULA con él —el
 *     papel no pasó, la herramienta no salió—;
 *   · lo asignado SIN papel (WhatsApp, anterior al 05/10) se DEVUELVE al
 *     pañol: eso sí pasó, tiene historia, y anularlo la borraría;
 *   · lo PRESTADO del pañol no se toca: vive en la pared y vuelve por ahí.
 */
export function planQuitarCaja(prestamos, entregas) {
  const papeles = (entregas || []).filter((e) => e && !e.anulado_en);
  const ids = new Set(papeles.map((e) => e.id));
  const asignadas = (prestamos || [])
    .filter((p) => p && p.definitivo && !p.devuelto_en && !p.anulado_en);
  return {
    papeles: papeles.map((e) => e.id),
    firmados: papeles.filter((e) => e.foto_path).length,
    anular: asignadas.filter((p) => p.entrega_id && ids.has(p.entrega_id)).map((p) => p.id),
    devolver: asignadas.filter((p) => !p.entrega_id || !ids.has(p.entrega_id)).map((p) => p.id),
    prestadas: (prestamos || [])
      .filter((p) => p && !p.definitivo && !p.devuelto_en && !p.anulado_en).length,
  };
}

/* ── LA DEVOLUCIÓN DE TODO (tickets 328 y 329, Nico 06/10) ────────────────────
 *
 * «Cuando un chofer es despedido o renuncia tengo que marcar que me devolvió
 * las cosas. Esas cosas se vuelven a poner en stock si están en condiciones y
 * queda la fecha de devolución y a quién se devolvió guardado.» Y de ahí sale
 * el aviso a Personal, que lo usa para la liquidación final.
 *
 * Se lista TODO lo que tiene —lo del catálogo, unidad por unidad, y lo escrito
 * a mano en sus papeles— y de cada cosa se dice cómo volvió. Lo que vuelve en
 * condiciones queda disponible en el pañol; lo roto y lo perdido no, y es lo
 * que Personal descuenta.
 *
 * Cada unidad del catálogo va en su renglón aunque sean dos iguales: de dos
 * guantes puede volver uno. */
export const ESTADOS_DEVOLUCION = {
  devuelta: "Volvió bien",
  rota: "Volvió rota",
  perdida: "No volvió",
};

export function itemsDeDevolucion(prestamos, entregas) {
  const out = [];
  for (const p of prestamos || []) {
    if (!p || p.devuelto_en || p.anulado_en) continue;
    out.push({
      clave: `p${p.id}`,
      prestamo_id: p.id,
      herramienta_id: p.herramienta_id,
      nombre: p.herramienta?.nombre || p.herramienta?.codigo || "Herramienta",
      codigo: p.herramienta?.codigo || "",
      cantidad: 1,
      prestada: !p.definitivo,
      estado: "devuelta",
    });
  }
  sueltosDe(entregas).forEach((x, i) => {
    out.push({
      clave: `s${x.entrega_id}-${i}`,
      entrega_id: x.entrega_id,
      nombre: x.nombre,
      codigo: "",
      cantidad: x.cantidad,
      estado: "devuelta",
    });
  });
  return out.sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), "es"));
}

/* Lo que viaja a la base: sólo lo que la función necesita. */
export function itemsDeDevolucionParaLaBase(items) {
  return (items || []).map((x) => (x.prestamo_id
    ? { prestamo_id: x.prestamo_id, estado: x.estado }
    : { nombre: x.nombre, cantidad: x.cantidad, entrega_id: x.entrega_id, estado: x.estado }));
}

export function resumenDevolucion(items) {
  const r = { total: 0, devuelta: 0, rota: 0, perdida: 0 };
  for (const x of items || []) {
    const n = Number(x.cantidad) || 1;
    r.total += n;
    if (r[x.estado] !== undefined) r[x.estado] += n;
  }
  r.faltantes = r.rota + r.perdida;
  return r;
}
